import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dropdown, IconName, Tooltip } from '@ets/design-system';
import { CustomIcon } from '_common/lib/components';
import { Action } from './ActionsSelector';

type ActionItem = {
  id: Action;
  text: string;
  textValues?: Record<string, string>;
  icon?: IconName;
  customIcon?: string;
  tooltipMessageId?: TranslationId;
} & (
  | {
      subMenu?: false;
      onClick: () => void;
      disabled?: boolean;

      subActions?: undefined;
    }
  | {
      subMenu: true;
      subActions: (ActionItem | undefined)[];

      onClick?: undefined;
      disabled?: undefined;
    }
);

type ActionItemProps = Omit<ActionItem, 'id'> & { children?: ReactNode; id: string };

const ActionItem = ({
  id,
  text,
  textValues,
  icon,
  customIcon,
  tooltipMessageId,
  subMenu,
  onClick,
  disabled,
  subActions,
  children,
}: ActionItemProps) => {
  const intl = useIntl();

  const CUSTOM_ICONS = {
    Word: <CustomIcon name="Word" size={24} />,
    Excel: <CustomIcon name="Excel" size={24} />,
    PowerPoint: <CustomIcon name="PowerPoint" size={24} />,
  };

  const iconComponent =
    customIcon && customIcon in CUSTOM_ICONS
      ? CUSTOM_ICONS[customIcon as keyof typeof CUSTOM_ICONS]
      : undefined;

  if (subMenu) {
    return (
      <Dropdown.Sub>
        <Dropdown.SubTrigger testId={`${id}-action-dropdown-submenu`} prefix={icon}>
          <FormattedMessage id={text} values={textValues} />
        </Dropdown.SubTrigger>
        <Dropdown.SubContent testId={`${id}-action-dropdown-submenu-content`}>
          <Tooltip
            placement="left"
            visible={disabled !== undefined ? !!disabled || !!tooltipMessageId : !!tooltipMessageId}
            content={
              tooltipMessageId
                ? intl.formatMessage({
                    id: tooltipMessageId,
                  })
                : ''
            }
            testId={`${id}-action-dropdown-subMenu-item`}
          >
            <div>
              {subActions?.map((action) => action && <ActionItem key={action.id} {...action} />)}
            </div>
          </Tooltip>
        </Dropdown.SubContent>
      </Dropdown.Sub>
    );
  }

  return (
    <Tooltip
      placement="left"
      visible={disabled !== undefined ? !!disabled || !!tooltipMessageId : !!tooltipMessageId}
      content={
        tooltipMessageId
          ? intl.formatMessage({
              id: tooltipMessageId,
            })
          : ''
      }
      testId={`${id}-action-dropdown-item`}
    >
      <Dropdown.Item
        prefix={icon}
        onClick={onClick}
        disabled={disabled}
        testId={`${id}-action-dropdown-item`}
      >
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {iconComponent && <div style={{ marginRight: '0.5rem' }}>{iconComponent}</div>}
          {children}
          <FormattedMessage id={text} values={textValues} />
        </div>
      </Dropdown.Item>
    </Tooltip>
  );
};

export default ActionItem;
